import { DateRangeTwoTone } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Stack,
  SxProps,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { addMonths, format } from 'date-fns';
import { tr } from 'date-fns/locale';
import type { DateRange } from 'mui-daterange-picker-plus';
import { PickerBase } from 'mui-daterange-picker-plus';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function DateRangeInput({
  value: [startDate, endDate],
  onChange,
}: {
  value: [Date | undefined, Date | undefined];
  onChange: (value: [Date | undefined, Date | undefined]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate,
    endDate,
  });
  const handleSetDateRangeOnChange = (dateRange: DateRange) => {
    setDateRange(dateRange);
    onChange([dateRange.startDate, dateRange.endDate]);
    handleClose();
  };

  function generatePredefinedRanges() {
    const today = new Date();
    const thisMonth = addMonths(today, 1);
    const nextMonth = new Date(
      thisMonth.getFullYear(),
      thisMonth.getMonth() + 1,
      1,
    );
    const nextMonth2 = addMonths(nextMonth, 1);
    const thisYear = new Date(today.getFullYear(), 11, 31);

    return [
      {
        label: t('dateRangeInput.thisMonth'),
        startDate: today,
        endDate: thisMonth,
      },
      {
        label: t('dateRangeInput.nextMonth'),
        startDate: nextMonth,
        endDate: nextMonth2,
      },
      {
        label: t('dateRangeInput.thisYear'),
        startDate: today,
        endDate: thisYear,
      },
    ];
  }

  function handleManualChange([startDate, endDate]: [Date?, Date?]) {
    if (startDate && endDate) {
      if (startDate > endDate) return;
      if (endDate < startDate) return;
    }
    setDateRange({ startDate, endDate });
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Paper
        elevation={open ? 1 : 0}
        ref={ref}
        sx={(t) => ({
          border: !open ? 'none' : 'initial',
          bgcolor: open ? 'grey.100' : 'transparent',
          transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        })}
      >
        <Stack
          direction="row"
          alignItems="end"
          spacing={1}
          sx={{
            px: 1,
            pt: open ? '4px' : 0,
            position: 'relative',
            top: open ? '-4px' : 0,
          }}
        >
          <DateInput
            label={t('dateRangeInput.startDate')}
            value={
              (dateRange.startDate &&
                format(dateRange.startDate || new Date(), 'yyyy-MM-dd')) ||
              ''
            }
            onChange={(val) =>
              handleManualChange([new Date(val), dateRange.endDate])
            }
            max={dateRange.endDate && format(dateRange.endDate, 'yyyy-MM-dd')}
            onFocus={handleOpen}
            onClick={handleOpen}
          />
          <DateInput
            label={t('dateRangeInput.endDate')}
            value={
              (dateRange.endDate &&
                format(dateRange.endDate || new Date(), 'yyyy-MM-dd')) ||
              ''
            }
            onChange={(val) =>
              handleManualChange([dateRange.startDate, new Date(val)])
            }
            min={
              dateRange.startDate && format(dateRange.startDate, 'yyyy-MM-dd')
            }
            onFocus={handleOpen}
            onClick={handleOpen}
          />
          <IconButton onClick={handleOpen}>
            <DateRangeTwoTone />
          </IconButton>
        </Stack>
        <Popper
          open={open}
          anchorEl={ref.current}
          sx={{ zIndex: 1000 }}
          disablePortal
        >
          <Box>
            <PickerBase
              definedRanges={generatePredefinedRanges()}
              hideOutsideMonthDays={false}
              initialDateRange={{
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
              }}
              onChange={(range: DateRange) => handleSetDateRangeOnChange(range)}
              locale={tr}
              labels={{
                predefinedRanges: t('dateRangeInput.quickSelect'),
                actions: {
                  apply: t('dateRangeInput.apply'),
                  cancel: t('dateRangeInput.cancel'),
                },
                footer: {
                  startDate: t('dateRangeInput.startDate'),
                  endDate: t('dateRangeInput.endDate'),
                },
              }}
            />
          </Box>
        </Popper>
      </Paper>
    </ClickAwayListener>
  );
}

function DateInput(props: {
  value: string;
  onChange: (val: string) => void;
  min?: string;
  max?: string;
  label?: string;
  sx?: SxProps;
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  return (
    <Box sx={{ position: isMobile ? 'relative' : 'none' }}>
      {isMobile && (
        <Box
          onClick={props.onClick}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '120px',
            height: '60px',
            zIndex: 3,
            cursor: 'pointer',
          }}
        />
      )}
      <TextField
        label={props.label}
        type="date"
        variant="standard"
        value={props.value}
        onClick={props.onClick}
        sx={{
          ...props.sx,
          minWidth: '120px',
          pointerEvents: isMobile ? 'none' : 'auto',
          zIndex: isMobile ? 1 : 'none',
          mt: !isMobile ? '5px' : '0px',
          input: {
            '&::-webkit-datetime-edit-month-field:focus': {
              color: (t) => t.palette.text.primary,
              bgcolor: (t) => t.palette.primary.main,
            },
            '&::-webkit-datetime-edit-day-field:focus': {
              color: (t) => t.palette.text.primary,
              bgcolor: (t) => t.palette.primary.main,
            },
            '&::-webkit-datetime-edit-year-field:focus': {
              color: (t) => t.palette.text.primary,
              bgcolor: (t) => t.palette.primary.main,
            },
            '&::-webkit-calendar-picker-indicator': {
              display: 'none',
            },
          },
          'input[value=""]:not(:focus)': {
            color: 'transparent',
          },
          ...(isMobile && {
            'input.Mui-disabled': {
              color: '#000',
              WebkitTextFillColor: '#000',
              bgcolor: 'transparent',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              color: '#000',
              WebkitTextFillColor: '#000',
            },
            '& .MuiFormLabel-root.Mui-disabled': {
              color: '#000',
            },
          }),
        }}
        inputProps={{
          onClick: props.onFocus,
          min: props.min,
          max: props.max,
          onFocus: props.onFocus,
          onBlur: props.onBlur,
        }}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Box>
  );
}
